import { Link } from 'react-router-dom';
import PageHero from '../components/ui/PageHero';
import {
  LuAccessibility, LuMonitor, LuKeyboard, LuEye,
  LuVolume2, LuSmartphone, LuMail, LuArrowLeft,
} from 'react-icons/lu';

const IMG = {
  hero: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=900&q=80&auto=format',
};

const FEATURES = [
  {
    icon: <LuKeyboard size={22}/>,
    title: 'Keyboard Navigation',
    desc: 'All interactive elements — links, buttons, forms, and navigation — are fully accessible via keyboard. Use Tab to move forward, Shift+Tab to move backward, and Enter or Space to activate.',
  },
  {
    icon: <LuEye size={22}/>,
    title: 'Colour Contrast',
    desc: 'Text and interactive elements meet or exceed WCAG 2.1 AA contrast ratio requirements. Our colour palette was chosen specifically to ensure readability for users with colour vision deficiencies.',
  },
  {
    icon: <LuVolume2 size={22}/>,
    title: 'Screen Reader Support',
    desc: 'Semantic HTML elements, ARIA labels, and descriptive alt text are used throughout the site to ensure compatibility with screen readers such as NVDA, JAWS, VoiceOver, and TalkBack.',
  },
  {
    icon: <LuMonitor size={22}/>,
    title: 'Responsive Design',
    desc: 'The website is fully responsive and adapts to all screen sizes, from large desktop displays to small mobile screens. Content remains readable and usable at any viewport size.',
  },
  {
    icon: <LuSmartphone size={22}/>,
    title: 'Touch & Mobile Accessibility',
    desc: 'All interactive elements have sufficient touch target sizes (minimum 44×44px) for users on touchscreen devices. Gestures are never required to access core content or functionality.',
  },
  {
    icon: <LuEye size={22}/>,
    title: 'Reduced Motion',
    desc: 'Users who have enabled the "prefers-reduced-motion" browser setting will experience reduced or eliminated animations across the site, respecting their comfort preferences.',
  },
];

const STANDARDS = [
  { label:'WCAG 2.1',          detail:'Web Content Accessibility Guidelines — our target standard' },
  { label:'Level AA',          detail:'Compliance level we aim to meet across all pages' },
  { label:'ARIA 1.1',          detail:'Accessible Rich Internet Applications specification' },
  { label:'Semantic HTML5',    detail:'Proper use of heading hierarchy, landmarks, and roles' },
  { label:'Alt Text',          detail:'Descriptive alternative text on all meaningful images' },
  { label:'Focus Indicators',  detail:'Visible keyboard focus rings on all interactive elements' },
];

function Accessibility() {
  return (
    <div className="page-enter">
      {/* Hero */}
      <PageHero
        tag="Accessibility"
        title="Accessibility Statement"
        subtitle="KI INNOVATION is committed to ensuring digital accessibility for people of all abilities. We continually improve the user experience for everyone. Last updated: July 2026."
        breadcrumb={[{ label: 'Accessibility' }]}
      >
        <div className="page-hero-img-card">
          <img src={IMG.hero} alt="Digital accessibility and inclusive design" loading="lazy" />
        </div>
      </PageHero>

      {/* Features */}
      <section className="page-section bg-dark-2">
        <div className="container">
          <div className="text-center reveal" style={{ marginBottom:48 }}>
            <div className="section-tag">Our Commitment</div>
            <h2 className="section-title">Accessibility Features</h2>
            <p className="section-sub" style={{ margin:'0 auto' }}>
              We have implemented the following features to make KI INNOVATION accessible
              to as many users as possible, regardless of ability or assistive technology used.
            </p>
          </div>

          <div className="acc-features-grid reveal">
            {FEATURES.map((f,i) => (
              <div key={i} className="acc-feature-card">
                <div className="acc-feature-icon">{f.icon}</div>
                <div>
                  <div className="acc-feature-title">{f.title}</div>
                  <div className="acc-feature-desc">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="divider"/>

      {/* Standards */}
      <section className="page-section">
        <div className="container">
          <div className="legal-layout">
            <aside className="legal-sidebar">
              <div className="legal-sidebar-card">
                <div className="legal-sidebar-title">Standards We Follow</div>
                <ul className="legal-sidebar-nav">
                  {STANDARDS.map((s,i) => (
                    <li key={i} style={{ display:'flex', flexDirection:'column', gap:2, padding:'8px 0', borderBottom: i < STANDARDS.length-1 ? '1px solid var(--bg-4)' : 'none' }}>
                      <span style={{ fontWeight:700, fontSize:'.83rem', color:'var(--text-2)' }}>{s.label}</span>
                      <span style={{ fontSize:'.78rem', color:'var(--text-muted)' }}>{s.detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            <main className="legal-main">
              <div className="legal-section">
                <div className="legal-section-header">
                  <span className="legal-section-icon"><LuAccessibility size={22}/></span>
                  <h2 className="legal-section-title">Our Conformance Status</h2>
                </div>
                <p className="legal-para">
                  KI INNOVATION aims to conform to the Web Content Accessibility Guidelines (WCAG) 2.1
                  at Level AA. These guidelines explain how to make web content more accessible to
                  people with disabilities. Conformance at Level AA means that the content satisfies
                  the majority of success criteria that address the most significant accessibility barriers.
                </p>
                <p className="legal-para">
                  We acknowledge that full accessibility is an ongoing journey. While we strive to meet
                  WCAG 2.1 AA standards across all pages, some content may not yet meet the full standard.
                  We are actively working to address any gaps identified.
                </p>
              </div>

              <div className="legal-section">
                <div className="legal-section-header">
                  <span className="legal-section-icon"><LuMonitor size={22}/></span>
                  <h2 className="legal-section-title">Known Limitations</h2>
                </div>
                <p className="legal-para">
                  Complex animated visualisations (such as the conveyor belt simulations) may not be
                  fully describable through alt text alone. We are working on adding expanded text
                  descriptions for all interactive and animated elements in a future update.
                  Users who have difficulty with animated content can use browser settings to
                  reduce motion effects.
                </p>
              </div>

              <div className="legal-section">
                <div className="legal-section-header">
                  <span className="legal-section-icon"><LuMail size={22}/></span>
                  <h2 className="legal-section-title">Report an Accessibility Issue</h2>
                </div>
                <p className="legal-para">
                  We welcome feedback on the accessibility of KI INNOVATION. If you encounter any
                  barriers that prevent you from accessing content, please contact us at{' '}
                  <a href="mailto:info@kiinnovation.in" style={{ color:'var(--primary)', fontWeight:600 }}>
                    info@kiinnovation.in
                  </a>
                  . Include a description of the issue, the page URL, and the assistive technology
                  you were using. We will respond within 5 business days and work to resolve the issue.
                </p>
              </div>

              <div className="legal-back-row">
                <Link to="/" className="btn btn-ghost">
                  <LuArrowLeft size={16}/> Back to Home
                </Link>
                <Link to="/contact" className="btn btn-primary">
                  <LuMail size={16}/> Report an Issue
                </Link>
              </div>
            </main>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Accessibility;
